if (!customElements.get('shoppable-video-carousel')) {
  customElements.define(
    'shoppable-video-carousel',
    class ShoppableVideoCarousel extends HTMLElement {
      connectedCallback() {
        this.initialize();
      }

      initialize() {
        if (this.isInitialized) return;

        this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
        this.previousButton = this.querySelector('[data-carousel-previous]');
        this.nextButton = this.querySelector('[data-carousel-next]');
        this.status = this.querySelector('[data-carousel-status]');
        this.videos = this.slides.map((slide) => slide.querySelector('video'));
        this.pointerStartX = null;

        const initialIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
        this.activeIndex = initialIndex >= 0 ? initialIndex : 0;

        this.previousButton?.addEventListener('click', () => this.changeSlide(-1));
        this.nextButton?.addEventListener('click', () => this.changeSlide(1));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.setActiveSlide(Number.parseInt(dot.dataset.index, 10)));
        });

        this.addEventListener('keydown', (event) => this.onKeydown(event));
        this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.addEventListener('pointerup', (event) => this.onPointerUp(event));
        this.addEventListener('pointercancel', () => {
          this.pointerStartX = null;
        });
        this.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event));

        const hasMultipleSlides = this.slides.length > 1;
        if (this.previousButton) this.previousButton.disabled = !hasMultipleSlides;
        if (this.nextButton) this.nextButton.disabled = !hasMultipleSlides;

        this.isInitialized = true;
        this.update();
      }

      changeSlide(direction) {
        if (this.slides.length < 2) return;

        const nextIndex = (this.activeIndex + direction + this.slides.length) % this.slides.length;
        this.setActiveSlide(nextIndex);
      }

      setActiveSlide(index) {
        if (!Number.isInteger(index) || this.slides.length === 0) return;

        this.activeIndex = (index + this.slides.length) % this.slides.length;
        this.update();
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.changeSlide(-1);
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.changeSlide(1);
        }
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.pointerStartX = event.clientX;
      }

      onPointerUp(event) {
        if (this.pointerStartX === null) return;

        const deltaX = event.clientX - this.pointerStartX;
        this.pointerStartX = null;
        if (Math.abs(deltaX) <= 40) return;

        this.changeSlide(deltaX < 0 ? 1 : -1);
      }

      onBlockSelect(event) {
        const selectedSlide = event.target.closest('[data-carousel-slide]');
        if (!selectedSlide || !this.contains(selectedSlide)) return;

        this.setActiveSlide(Number.parseInt(selectedSlide.dataset.index, 10));
      }

      update() {
        this.slides.forEach((slide, index) => {
          const position = this.getCircularPosition(index);
          const isActive = index === this.activeIndex;

          slide.classList.toggle('is-active', isActive);
          slide.classList.toggle('is-prev', position === -1);
          slide.classList.toggle('is-next', position === 1);
          slide.classList.toggle('is-prev-far', position === -2);
          slide.classList.toggle('is-next-far', position === 2);
          slide.classList.toggle('is-distant', Math.abs(position) > 2);
          slide.setAttribute('aria-hidden', String(!isActive));
          slide.inert = !isActive;
        });

        this.dots.forEach((dot, index) => {
          const isActive = index === this.activeIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.status) {
          this.status.textContent =
            this.slides.length > 0 ? `${this.activeIndex + 1} / ${this.slides.length}` : '0 / 0';
        }

        this.syncPlayback();
      }

      getCircularPosition(index) {
        const count = this.slides.length;
        if (count === 0) return 0;

        let position = (index - this.activeIndex + count) % count;
        if (position > count / 2) position -= count;
        return position;
      }

      syncPlayback() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.videos.forEach((video, index) => {
          if (!video) return;

          if (index === this.activeIndex && !reduceMotion) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }
    }
  );
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('shoppable-video-carousel').forEach((carousel) => carousel.initialize());
  });
}
